import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, FolderOpen } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, ClosingCta, CheckList, faqSchema, usePrimaryCta } from '../components/common/B2B';
import { Section, ItemsSection, FaqSection, HeroChecks, TextLink, RowsTable } from '../components/common/B2BSections';
import { HubDiagram } from '../components/common/Diagram';

// Interoperabilidad (nov-2026), contenido compuesto desde cero: las cinco formas
// de conectarse ordenadas por esfuerzo (de la API a no programar nada), los casos
// por sistema (TMS, ERP, flota/PDA, portal), la interoperabilidad entre empresas
// (partes sin cuenta, documentos de otros sistemas, autoridades, datos eFTI),
// seguridad y datos sin ataduras, y FAQ. No repite la llamada a la API de la
// página de proveedores de software, a la que enlaza.
// Importaciones: cualquier fichero, también PDF (Jesús, 4-nov), nunca «CSV» a secas.
// eFTI: la fecha ya publicada en la web (las autoridades, desde julio de 2027);
// nunca decir que BlueCMR es plataforma eFTI certificada.
// El formulario propio se sustituye por la reserva común con ?origin=integracion.
// Hero: terminal de mano en el muelle (GPT Image 2, marketing/prompts-imagenes/hero-interoperabilidad-pda.txt).

const HERO_IMAGE = { src: '/images/interoperabilidad/hero-pda-muelle.webp', position: '50% 50%' };
const PAGE_URL = { es: 'https://www.bluecmr.com/interoperabilidad', en: 'https://www.bluecmr.com/en/interoperability' };
const QUERY = '?origin=integracion';
const DEVELOPER_PORTAL = { es: '/es/developers/info', en: '/en/developers/info' };
const EFTI_ARTICLE = '/articulos/digitalizacion-documento-control-transporte-datos-efti';

const COPY = {
  es: {
    seoTitle: 'Integración y API para documentos de transporte | BlueCMR',
    seoDescription: 'Conecte BlueCMR con su TMS, ERP o portal de clientes: API REST, webhooks, enlaces embebidos y ficheros en cualquier formato, con datos según eFTI.',
    seoKeywords: 'interoperabilidad transporte, integración documentos de transporte, api documentos de transporte, webhooks transporte, integración tms erp, efti',
    heroEyebrow: 'Interoperabilidad',
    heroTitle: 'Conectado con sus sistemas y con los de todas las partes',
    heroText: 'BlueCMR se integra con su TMS, ERP, sistema de flota o portal de clientes, y comparte cada documento con cargadores, transportistas, destinatarios y autoridades, tengan el sistema que tengan.',
    heroChecks: ['API REST y webhooks', 'Ficheros en cualquier formato', 'Datos conformes al marco eFTI'],
    heroSecondary: 'Portal de desarrolladores',
    hub: {
      eyebrow: 'Cómo encaja',
      title: 'Entre sus sistemas y los de todas las partes',
      left: { title: 'Sus sistemas', items: [
        { title: 'TMS o ERP', text: 'API REST y webhooks' },
        { title: 'Ficheros', text: 'Por FTP o correo, en cualquier formato' },
        { title: 'Apps, PDA y portales', text: 'Enlaces embebidos' },
      ] },
      center: { title: 'BlueCMR', text: 'Crea, envía a firma, sigue y conserva cada documento, con sus evidencias.', items: ['eCMR · Carta de porte · DeCA · ADR', 'Datos según el estándar eFTI'] },
      right: { title: 'Las demás partes', items: [
        { title: 'Cargadores, transportistas y destinatarios', text: 'Por enlace, QR o correo, sin cuenta' },
        { title: 'Autoridades', text: 'Abren el documento con su QR' },
        { title: 'Otras empresas', text: 'Sus documentos entran en su Archivo' },
      ] },
      caption: 'La información va en los dos sentidos: los documentos salen de sus sistemas y cada firma, evento o cambio de estado vuelve a ellos.',
    },
    waysEyebrow: 'Formas de conectarse',
    waysTitle: 'De la API a no programar nada',
    waysText: 'Elija según lo que tenga hoy: cada forma sirve sola o junto a las demás.',
    waysHead: ['Forma', 'Qué hace', 'Esfuerzo'],
    ways: [
      { title: 'API REST', text: 'Su TMS o ERP crea, modifica, envía y consulta documentos. Autenticación por API key, entorno de pruebas y reintentos seguros.', effort: 'Desarrollo en su sistema' },
      { title: 'Webhooks', text: 'Cada firma, incidencia, transferencia o cambio de estado llega a su sistema en el momento en que ocurre.', effort: 'Desarrollo en su sistema' },
      { title: 'Enlaces embebidos', text: 'La firma y la consulta del documento dentro de sus apps, PDA o portal de clientes, sin cambiar de pantalla.', effort: 'Poco desarrollo' },
      { title: 'Ficheros', text: 'Los ficheros que ya genera su sistema, en cualquier formato e incluso en PDF, llegan por FTP o por correo y se convierten en documentos.', effort: 'Sin desarrollo' },
      { title: 'Desde la plataforma', text: 'Importación de ficheros de cualquier tipo, plantillas y envío masivo, para empezar el primer día.', effort: 'Sin desarrollo' },
    ],
    waysLink: 'Integrar BlueCMR en un producto de software',
    systems: {
      eyebrow: 'Casos habituales',
      title: 'Según el sistema que use',
      cols: 4,
      items: [
        { title: 'TMS', text: 'Cada servicio genera su documento, y las firmas, incidencias y cambios vuelven al servicio sin teclearlos.' },
        { title: 'ERP', text: 'El pedido o el albarán de salida se convierte en documento, y la entrega firmada queda asociada al pedido para facturar.' },
        { title: 'Flota y PDA', text: 'El conductor o el operario abre, enseña y firma el documento desde la aplicación o el terminal que ya usa.' },
        { title: 'Portal de clientes', text: 'Sus clientes consultan y descargan sus documentos firmados desde su portal, con un visor embebido.' },
      ],
    },
    between: {
      eyebrow: 'Entre empresas',
      title: 'Interoperable también fuera de su empresa',
      text: 'Un documento de transporte pasa por varias empresas. Ninguna tiene que usar el mismo sistema que usted.',
      cols: 4,
      items: [
        { title: 'Las demás partes, sin cuenta', text: 'Cargadores, transportistas y destinatarios reciben el documento por correo, SMS, WhatsApp, QR o enlace, y firman desde el navegador.' },
        { title: 'Lo que llega de otros sistemas', text: 'Los documentos que le entregan otras empresas se archivan leyendo su QR, con el enlace o desde el PDF, y la IA extrae sus datos.' },
        { title: 'Las autoridades', text: 'En una inspección, el agente abre el documento con su QR, sin instalar nada.' },
        { title: 'Datos, no solo un PDF', text: 'Cada documento guarda sus datos según el conjunto común de datos de eFTI y la semántica de UN/CEFACT.', to: EFTI_ARTICLE, link: 'Qué es eFTI' },
      ],
    },
    secTitle: 'Integraciones seguras',
    secText: 'Cada integración con sus credenciales y sus permisos. BLUECMR, S.L.U. dispone de un sistema de gestión certificado de acuerdo al RD 311/2022 (ENS) por OCA CERT.',
    secItems: [
      'Autenticación por API key, con entornos de pruebas y de producción separados',
      'Permisos mínimos por usuario y función',
      'Cifrado en tránsito y en reposo',
      'Datos alojados en la Unión Europea',
      'Registro de actividad y auditoría de seguridad',
    ],
    secLink: 'Seguridad',
    openTitle: 'Sus datos, siempre suyos',
    openText: 'BlueCMR se añade a sus sistemas, no los sustituye, y nada de lo que guarda queda atrapado en la plataforma.',
    openItems: [
      'PDF firmado que se verifica fuera de BlueCMR',
      'Exportación de documentos y datos para llevarlos a otros sistemas',
      'Informe de evidencias de cada documento',
      'Sin cambiar de TMS ni de ERP',
    ],
    openLink: 'Firma y conservación',
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Antes de integrar',
    faq: [
      { q: '¿Necesito un desarrollador para integrar BlueCMR?', a: 'No siempre. Con ficheros por FTP o por correo, o desde la propia plataforma, no hace falta programar. La API y los webhooks sí requieren desarrollo en su sistema o en el de su proveedor de software.' },
      { q: '¿Puede hacer la integración mi proveedor de TMS o ERP?', a: 'Sí. Tiene la documentación y el entorno de pruebas en el portal de desarrolladores, y le acompañamos durante el desarrollo.' },
      { q: '¿Qué formatos de fichero se pueden importar?', a: 'Cualquiera, incluso PDF. Los ficheros llegan por FTP, por correo o desde la plataforma y se convierten en documentos.' },
      { q: '¿Hay que cambiar de TMS o de ERP?', a: 'No. BlueCMR se conecta con los sistemas que ya usa y se ocupa solo de los documentos de transporte.' },
      { q: '¿Qué tiene que ver con eFTI?', a: 'El Reglamento (UE) 2020/1056 (eFTI) obliga a las autoridades a aceptar, desde julio de 2027, la información de transporte que llegue por plataformas eFTI certificadas. BlueCMR ya guarda los datos de cada documento según el conjunto común de datos de eFTI, preparados para ese intercambio.' },
    ],
    closing: 'Cuéntenos qué sistemas usa',
    closingText: 'Le enseñamos cómo encaja BlueCMR con ellos, sin compromiso.',
    breadcrumb: ['Inicio', 'Plataforma', 'Interoperabilidad'],
  },
  en: {
    seoTitle: 'Integration and API for transport documents | BlueCMR',
    seoDescription: 'Connect BlueCMR to your TMS, ERP or customer portal: REST API, webhooks, embedded links and files in any format, with data following eFTI.',
    seoKeywords: 'transport interoperability, transport document integration, transport document api, transport webhooks, tms erp integration, efti',
    heroEyebrow: 'Interoperability',
    heroTitle: 'Connected to your systems and to everyone else\'s',
    heroText: 'BlueCMR integrates with your TMS, ERP, fleet system or customer portal, and shares every document with shippers, carriers, consignees and authorities, whatever system they use.',
    heroChecks: ['REST API and webhooks', 'Files in any format', 'Data aligned with eFTI'],
    heroSecondary: 'Developer portal',
    hub: {
      eyebrow: 'How it fits',
      title: 'Between your systems and everyone else\'s',
      left: { title: 'Your systems', items: [
        { title: 'TMS or ERP', text: 'REST API and webhooks' },
        { title: 'Files', text: 'By FTP or email, in any format' },
        { title: 'Apps, handhelds and portals', text: 'Embedded links' },
      ] },
      center: { title: 'BlueCMR', text: 'Creates, sends for signature, tracks and preserves every document, with its evidence.', items: ['eCMR · Consignment note · Control document · ADR', 'Data following the eFTI standard'] },
      right: { title: 'The other parties', items: [
        { title: 'Shippers, carriers and consignees', text: 'By link, QR code or email, no account' },
        { title: 'Authorities', text: 'Open the document with its QR code' },
        { title: 'Other companies', text: 'Their documents go into your Archive' },
      ] },
      caption: 'Information flows both ways: documents come out of your systems, and every signature, event or status change flows back into them.',
    },
    waysEyebrow: 'Ways to connect',
    waysTitle: 'From the API to no coding at all',
    waysText: 'Choose according to what you have today: each option works on its own or together with the others.',
    waysHead: ['Option', 'What it does', 'Effort'],
    ways: [
      { title: 'REST API', text: 'Your TMS or ERP creates, modifies, sends and queries documents. API key authentication, test environment and safe retries.', effort: 'Development in your system' },
      { title: 'Webhooks', text: 'Every signature, incident, transfer or status change reaches your system the moment it happens.', effort: 'Development in your system' },
      { title: 'Embedded links', text: 'Signing and viewing the document inside your apps, handhelds or customer portal, without switching screens.', effort: 'Little development' },
      { title: 'Files', text: 'The files your system already produces, in any format and even as PDF, arrive by FTP or email and become documents.', effort: 'No development' },
      { title: 'From the platform', text: 'Import of files of any type, templates and bulk sending, to start on day one.', effort: 'No development' },
    ],
    waysLink: null,
    systems: {
      eyebrow: 'Common cases',
      title: 'Depending on the system you use',
      cols: 4,
      items: [
        { title: 'TMS', text: 'Each job generates its document, and signatures, incidents and changes flow back to the job without retyping.' },
        { title: 'ERP', text: 'The order or dispatch note becomes a document, and the signed delivery is linked to the order for invoicing.' },
        { title: 'Fleet and handhelds', text: 'The driver or operator opens, shows and signs the document from the app or terminal they already use.' },
        { title: 'Customer portal', text: 'Your customers view and download their signed documents from your portal, with an embedded viewer.' },
      ],
    },
    between: {
      eyebrow: 'Between companies',
      title: 'Interoperable outside your company too',
      text: 'A transport document passes through several companies. None of them has to use the same system as you.',
      cols: 4,
      items: [
        { title: 'Other parties, no account', text: 'Shippers, carriers and consignees receive the document by email, SMS, WhatsApp, QR code or link, and sign from the browser.' },
        { title: 'What comes from other systems', text: 'Documents handed to you by other companies are archived by scanning their QR code, with the link or from the PDF, and AI extracts their data.' },
        { title: 'The authorities', text: 'At a roadside check, the officer opens the document with its QR code, with nothing to install.' },
        { title: 'Data, not just a PDF', text: 'Every document stores its data following the eFTI common data set and UN/CEFACT semantics.' },
      ],
    },
    secTitle: 'Secure integrations',
    secText: 'Each integration with its own credentials and permissions. BLUECMR, S.L.U. holds a management system certified under Royal Decree 311/2022 (ENS) by OCA CERT.',
    secItems: [
      'API key authentication, with separate test and production environments',
      'Least-privilege permissions per user and role',
      'Encryption in transit and at rest',
      'Data hosted in the European Union',
      'Activity log and security audit trail',
    ],
    secLink: 'Security',
    openTitle: 'Your data, always yours',
    openText: 'BlueCMR adds to your systems rather than replacing them, and nothing it stores is locked into the platform.',
    openItems: [
      'Signed PDF that can be verified outside BlueCMR',
      'Export of documents and data to take them to other systems',
      'Evidence report for every document',
      'No need to change your TMS or ERP',
    ],
    openLink: 'Signature and preservation',
    faqEyebrow: 'FAQ',
    faqTitle: 'Before integrating',
    faq: [
      { q: 'Do I need a developer to integrate BlueCMR?', a: 'Not always. With files by FTP or email, or from the platform itself, no coding is needed. The API and webhooks do require development in your system or your software provider\'s.' },
      { q: 'Can my TMS or ERP provider build the integration?', a: 'Yes. The documentation and test environment are on the developer portal, and we support them during development.' },
      { q: 'Which file formats can be imported?', a: 'Any, even PDF. Files arrive by FTP, by email or from the platform and become documents.' },
      { q: 'Do I have to change my TMS or ERP?', a: 'No. BlueCMR connects to the systems you already use and only takes care of the transport documents.' },
      { q: 'How does it relate to eFTI?', a: 'Regulation (EU) 2020/1056 (eFTI) requires authorities to accept, from July 2027, transport information submitted through certified eFTI platforms. BlueCMR already stores each document\'s data following the eFTI common data set, ready for that exchange.' },
    ],
    closing: 'Tell us which systems you use',
    closingText: 'We will show you how BlueCMR fits with them, with no commitment.',
    breadcrumb: ['Home', 'Platform', 'Interoperability'],
  },
};

const InteroperabilidadPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const { getLocalizedUrl } = useLocalizedUrl();
  const primaryCta = usePrimaryCta(lang);
  const portal = DEVELOPER_PORTAL[lang];

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[1], url: lang === 'es' ? 'https://www.bluecmr.com/plataforma' : 'https://www.bluecmr.com/en/platform' },
          { name: c.breadcrumb[2], url: PAGE_URL[lang] },
        ]}
        structuredData={[faqSchema(c.faq)]}
      />

      <div className="bg-white">
        <PageHero
          eyebrow={c.heroEyebrow}
          title={c.heroTitle}
          text={c.heroText}
          image={HERO_IMAGE}
          primary={{ ...primaryCta, to: `${primaryCta.to}${QUERY}` }}
          secondary={{ href: portal, label: c.heroSecondary }}
          compact
        >
          <HeroChecks items={c.heroChecks} />
        </PageHero>

        {/* Cómo encaja: esquema resumen (Jesús: «no me niego a esquemas si aportan») */}
        <Section tone="gray">
          <SectionIntro eyebrow={c.hub.eyebrow} title={c.hub.title} className="max-w-3xl mb-10" />
          <HubDiagram label={c.hub.title} left={c.hub.left} center={c.hub.center} right={c.hub.right} caption={c.hub.caption} />
        </Section>

        {/* Formas de conectarse, por esfuerzo */}
        <Section tone="white">
          <SectionIntro eyebrow={c.waysEyebrow} title={c.waysTitle} text={c.waysText} />
          <RowsTable head={c.waysHead} rows={c.ways.map((w) => ({ title: w.title, text: w.text, aside: w.effort }))} />
          {c.waysLink && <TextLink to="/proveedores-software" className="mt-10">{c.waysLink}</TextLink>}
        </Section>

        <ItemsSection {...c.systems} tone="gray" />
        <ItemsSection {...c.between} tone="white" />

        {/* Seguridad y datos sin ataduras */}
        <Section tone="gray">
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-14">
            {[
              { icon: ShieldCheck, title: c.secTitle, text: c.secText, items: c.secItems, link: c.secLink, to: getLocalizedUrl('/confianza/seguridad') },
              { icon: FolderOpen, title: c.openTitle, text: c.openText, items: c.openItems, link: c.openLink, to: `${getLocalizedUrl('/confianza/firma-electronica')}#conservacion` },
            ].map((block) => {
              const Icon = block.icon;
              return (
                <div key={block.title} className="border-t-2 border-slate-900 pt-6">
                  <Icon className="w-7 h-7 text-primary-700 mb-5" strokeWidth={1.5} />
                  <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{block.title}</h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">{block.text}</p>
                  <CheckList items={block.items} className="mt-6 space-y-2.5" />
                  <TextLink to={block.to} className="mt-7">{block.link}</TextLink>
                </div>
              );
            })}
          </div>
        </Section>

        <FaqSection eyebrow={c.faqEyebrow} title={c.faqTitle} items={c.faq} tone="white" />

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} query={QUERY} />
      </div>
    </>
  );
};

export default InteroperabilidadPage;
